import { c as _c } from "react/compiler-runtime";
import { parseAsString, useQueryStates } from 'nuqs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type CartDrawerReturnFocusRef, useCartDrawer } from '@sola/ark-skippy/contexts/cart-drawer.tsx';
import { useCart } from '@sola/cart-sdk/store.tsx';
import { GiftTypeEnum } from '@sola/schema/dotcom/cart/gift.ts';
import type { ChildGiftLimitItem } from '@sola/schema/dotcom/proxy-api/giftLimits.ts';
import { type GraphQLCommitment, type GraphQLSupporterGiftLimits } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { areAllCommitmentsCorrespondent, filterActiveSponsorshipCommitments, isSponsorshipInTransition } from '@sola/shared-utils/supporters/commitments.ts';
import { useReadyForQueries, useSupporterQuery } from '@sola/supporter-sdk/hooks/base.ts';
import { commitmentsQuery, giftLimitsQuery } from '@sola/supporter-sdk/queries.ts';
import { ButtonAction, type GiftOption } from '../../types/gifts.ts';
import { formatGiftOptions } from '../../utils/gift-availability/gift-availability.ts';
import { buildGiftCartItemPayload } from '../../utils/gift-cart/gift-cart-payload.ts';
import type { GiftOptionsSubmitData } from '../gift-options-dialog/gift-options-dialog.tsx';
export const getCardElementKey = (participantId: string, giftType: string) => `${giftType.toLowerCase()}-${participantId}`;
const parseGiftType = (giftTypeString: string | null): GiftTypeEnum | null => {
  if (!giftTypeString) {
    return null;
  }
  return Object.values(GiftTypeEnum).find(value => value.toLowerCase() === giftTypeString.toLowerCase()) ?? null;
};
export interface SelectedGiftOptions {
  commitment: GraphQLCommitment;
  giftOption: GiftOption;
  giftType: GiftTypeEnum;
  returnFocusRef?: CartDrawerReturnFocusRef;
}
export interface RemovedGift {
  cartItemId: string;
  childName: string;
  giftType: GiftTypeEnum;
  returnFocusRef: CartDrawerReturnFocusRef;
}
export const getSelectedGiftTypeLimits = (giftLimits: GraphQLSupporterGiftLimits | undefined, selectedGiftOptions: SelectedGiftOptions | null): ChildGiftLimitItem | undefined => {
  if (!giftLimits || !selectedGiftOptions) {
    return undefined;
  }
  const {
    commitment,
    giftOption,
    giftType
  } = selectedGiftOptions;
  const childGlobalId = commitment.participant?.globalId ?? commitment.globalId;
  const giftLimitGroup = giftType === GiftTypeEnum.Center ? giftLimits.projects.find(project => project.id === giftOption.centerId) : giftLimits.children.find(child => child.id === childGlobalId);
  return giftLimitGroup?.giftTypes[giftType] ??
  // TODO: Is it wise to ever return the defaults for a Final Gift?
  giftLimits.defaults[giftType] ?? undefined;
};
const bulkGiftTypes = new Set<GiftTypeEnum>([GiftTypeEnum.Child, GiftTypeEnum.Family, GiftTypeEnum.Center]);
const getCommitmentKey = (commitment: GraphQLCommitment): string => commitment.internalId ?? commitment.participant?.globalId ?? commitment.globalId ?? '';
export const getEligibleBulkGiftSelections = (childGiftOptions: ReturnType<typeof formatGiftOptions>, primarySelection: SelectedGiftOptions | null, giftLimits?: GraphQLSupporterGiftLimits): SelectedGiftOptions[] => {
  if (!primarySelection || !bulkGiftTypes.has(primarySelection.giftType)) {
    return [];
  }
  const {
    commitment,
    giftOption,
    giftType
  } = primarySelection;
  const primaryCommitmentKey = getCommitmentKey(commitment);
  const seenCenterIds = new Set<string>();
  if (giftType === GiftTypeEnum.Center && giftOption.centerId) {
    seenCenterIds.add(giftOption.centerId);
  }
  return childGiftOptions[giftType].reduce<SelectedGiftOptions[]>((eligibleSelections, option) => {
    if (getCommitmentKey(option.commitment) === primaryCommitmentKey) {
      return eligibleSelections;
    }
    const isEligible = option.giftOption.canGive && option.giftOption.buttonAction !== null && option.giftOption.buttonAction !== ButtonAction.Edit && !option.giftOption.cartItem;
    if (!isEligible) {
      return eligibleSelections;
    }
    const selection: SelectedGiftOptions = {
      ...option,
      giftType
    };
    const selectionLimits = getSelectedGiftTypeLimits(giftLimits, selection);
    const minimumGiftAmount = selection.giftOption.paymentOptionsMinMax.oneTime.minAmount;
    if (selectionLimits && selectionLimits.remainingAmount < minimumGiftAmount) {
      return eligibleSelections;
    }
    if (giftType === GiftTypeEnum.Center) {
      const {
        centerId
      } = option.giftOption;
      if (!centerId || seenCenterIds.has(centerId)) {
        return eligibleSelections;
      }
      seenCenterIds.add(centerId);
    }
    eligibleSelections.push(selection);
    return eligibleSelections;
  }, []);
};
export const capGiftAmountAtRemainingLimit = (amount: number, giftTypeLimits: ChildGiftLimitItem | undefined): number => {
  const remainingAmount = giftTypeLimits?.remainingAmount;
  return remainingAmount && remainingAmount > 0 ? Math.min(amount, remainingAmount) : amount;
};
export const useChildGiftsLogic = t0 => {
  const $ = _c(78);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const childCenterMap = t1;
  const {
    t
  } = useTranslation("my");
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      id: parseAsString,
      type: parseAsString
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [t3, setActiveQuery] = useQueryStates(t2);
  const {
    id: activeChildId,
    type: activeGiftType
  } = t3;
  const {
    globalId
  } = useReadyForQueries();
  const {
    actions: t4
  } = useCart("actions");
  const {
    addItems,
    removeItem,
    updateItem
  } = t4;
  const {
    cart
  } = useCart("cart");
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [giftLimitsQuery, commitmentsQuery];
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const {
    data: t6,
    isLoading,
    isPending,
    isError
  } = useSupporterQuery(t5);
  const [giftLimits, commitments] = t6;
  let t7;
  if ($[4] !== commitments) {
    t7 = commitments?.filter(_temp);
    $[4] = commitments;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const giftCommitments = t7;
  const isGiftListLoading = !isError && (!giftLimits || !giftCommitments) && (isPending || isLoading);
  const hasCommitments = !!commitments?.length;
  let t8;
  if ($[6] !== commitments) {
    t8 = commitments ?? [];
    $[6] = commitments;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== t8) {
    t9 = areAllCommitmentsCorrespondent(t8);
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const hasOnlyCorrespondents = t9;
  cart?.items;
  let t10;
  if ($[10] !== cart?.items) {
    t10 = cart?.items ?? [];
    $[10] = cart?.items;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== childCenterMap || $[13] !== giftCommitments || $[14] !== giftLimits || $[15] !== t10) {
    t11 = formatGiftOptions(giftLimits, giftCommitments, t10, childCenterMap);
    $[12] = childCenterMap;
    $[13] = giftCommitments;
    $[14] = giftLimits;
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const childGiftOptions = t11;
  const shouldShowFinalGiftSection = isGiftListLoading || childGiftOptions[GiftTypeEnum.Final].length > 0;
  let t12;
  if ($[17] !== giftCommitments || $[18] !== isGiftListLoading) {
    t12 = isGiftListLoading || filterActiveSponsorshipCommitments(giftCommitments ?? []).length > 0;
    $[17] = giftCommitments;
    $[18] = isGiftListLoading;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const shouldShowOtherGiftSections = t12;
  const [selectedGiftOptions, setSelectedGiftOptions] = useState(null);
  const {
    openCartDrawer,
    clearCartDrawerAlert
  } = useCartDrawer();
  let t13;
  bb0: {
    if (!activeChildId || !activeGiftType || isLoading) {
      t13 = null;
      break bb0;
    }
    let t14;
    if ($[20] !== activeGiftType) {
      t14 = parseGiftType(activeGiftType);
      $[20] = activeGiftType;
      $[21] = t14;
    } else {
      t14 = $[21];
    }
    const giftType = t14;
    if (!giftType) {
      t13 = null;
      break bb0;
    }
    let t15;
    if ($[22] !== childGiftOptions || $[23] !== giftType) {
      t15 = childGiftOptions?.[giftType] ?? [];
      $[22] = childGiftOptions;
      $[23] = giftType;
      $[24] = t15;
    } else {
      t15 = $[24];
    }
    const list = t15;
    let t16;
    if ($[25] !== activeChildId || $[26] !== list) {
      let t17;
      if ($[28] !== activeChildId) {
        t17 = t18 => {
          const {
            commitment
          } = t18;
          return commitment.participant?.globalId === activeChildId;
        };
        $[28] = activeChildId;
        $[29] = t17;
      } else {
        t17 = $[29];
      }
      t16 = list.find(t17);
      $[25] = activeChildId;
      $[26] = list;
      $[27] = t16;
    } else {
      t16 = $[27];
    }
    const match = t16;
    let t17;
    if ($[30] !== giftType || $[31] !== match) {
      t17 = match ? {
        commitment: match.commitment,
        giftOption: match.giftOption,
        giftType
      } : null;
      $[30] = giftType;
      $[31] = match;
      $[32] = t17;
    } else {
      t17 = $[32];
    }
    t13 = t17;
  }
  const querySelection = t13;
  const activeSelection = selectedGiftOptions ?? querySelection;
  let t14;
  if ($[33] !== activeSelection || $[34] !== giftLimits) {
    t14 = getSelectedGiftTypeLimits(giftLimits, activeSelection);
    $[33] = activeSelection;
    $[34] = giftLimits;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const selectedGiftTypeLimits = t14;
  let t15;
  if ($[36] !== activeSelection || $[37] !== childGiftOptions || $[38] !== giftLimits) {
    t15 = getEligibleBulkGiftSelections(childGiftOptions, activeSelection, giftLimits);
    $[36] = activeSelection;
    $[37] = childGiftOptions;
    $[38] = giftLimits;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  const eligibleBulkGiftSelections = t15;
  let t16;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = selection => {
      setSelectedGiftOptions(selection);
    };
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  const handleGiftOptionsOpen = t16;
  let t17;
  if ($[41] !== activeSelection || $[42] !== addItems || $[43] !== clearCartDrawerAlert || $[44] !== eligibleBulkGiftSelections || $[45] !== giftLimits || $[46] !== globalId || $[47] !== openCartDrawer || $[48] !== t || $[49] !== updateItem) {
    t17 = async t18 => {
      const {
        amount: t19,
        frequency,
        addToAllEligible
      } = t18;
      const amount = t19 === undefined ? 0 : t19;
      clearCartDrawerAlert();
      if (!activeSelection) {
        throw new Error("Unable to add gift without selected gift options");
      }
      const {
        giftOption
      } = activeSelection;
      const cartItemId = giftOption.cartItem?.cartId;
      const isExistingItem = !!cartItemId;
      if (isExistingItem) {
        const updateErrors = await updateItem(cartItemId, {
          unitPrice: amount,
          commitmentFrequency: frequency
        });
        if (updateErrors?.length) {
          throw new Error("Unable to update gift in cart");
        }
      }
      const additionalSelections = addToAllEligible ? eligibleBulkGiftSelections : [];
      const selectionsToAdd = [...(!isExistingItem ? [activeSelection] : []), ...additionalSelections];
      let hasAdjustedBulkGiftAmount = false;
      if (selectionsToAdd.length > 0) {
        const giftCartItems = selectionsToAdd.map(selection_0 => {
          const selectionLimits = getSelectedGiftTypeLimits(giftLimits, selection_0);
          const giftAmount = capGiftAmountAtRemainingLimit(amount, selectionLimits);
          if (additionalSelections.includes(selection_0) && giftAmount < amount) {
            hasAdjustedBulkGiftAmount = true;
          }
          return buildGiftCartItemPayload({
            amount: giftAmount,
            centerId: selection_0.giftOption.centerId,
            commitment: selection_0.commitment,
            frequency,
            giftType: selection_0.giftType
          });
        });
        const addItemErrors = await addItems({
          items: giftCartItems,
          supporterId: globalId
        });
        if (addItemErrors?.length) {
          throw new Error("Unable to add gift to cart");
        }
      }
      openCartDrawer(hasAdjustedBulkGiftAmount ? {
        returnFocusRef: activeSelection.returnFocusRef,
        alert: {
          variant: "neutral",
          title: t("my:gifts.bulkGiftLimitAlert.title"),
          message: t("my:gifts.bulkGiftLimitAlert.message", {
            giftType: t(`my:gifts.typeLabels.${activeSelection.giftType.toLowerCase()}`)
          })
        }
      } : {
        returnFocusRef: activeSelection.returnFocusRef
      });
    };
    $[41] = activeSelection;
    $[42] = addItems;
    $[43] = clearCartDrawerAlert;
    $[44] = eligibleBulkGiftSelections;
    $[45] = giftLimits;
    $[46] = globalId;
    $[47] = openCartDrawer;
    $[48] = t;
    $[49] = updateItem;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  const handleGiftOptionsSubmit = t17;
  let t18;
  if ($[51] !== openCartDrawer || $[52] !== removeItem || $[53] !== t) {
    t18 = async t19 => {
      const {
        cartItemId: cartItemId_0,
        childName,
        giftType: giftType_0,
        returnFocusRef
      } = t19;
      const removeItemErrors = await removeItem(cartItemId_0);
      if (removeItemErrors?.length) {
        throw new Error("Unable to remove gift from cart");
      }
      openCartDrawer({
        returnFocusRef,
        alert: {
          variant: "positive",
          message: t("my:gifts.removeGiftSuccess.message", {
            childName,
            giftType: t(`my:gifts.typeLabels.${giftType_0.toLowerCase()}`)
          })
        }
      });
    };
    $[51] = openCartDrawer;
    $[52] = removeItem;
    $[53] = t;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  const handleGiftRemove = t18;
  let t19;
  if ($[55] !== setActiveQuery) {
    t19 = open => {
      if (open) {
        return;
      }
      setSelectedGiftOptions(null);
      setActiveQuery({
        id: null,
        type: null
      });
    };
    $[55] = setActiveQuery;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  const handleGiftOptionsOpenChange = t19;
  const lastScrolledKeyRef = useRef(null);
  let t20;
  let t21;
  if ($[57] !== activeChildId || $[58] !== querySelection || $[59] !== selectedGiftOptions) {
    t20 = () => {
      if (!querySelection || selectedGiftOptions || !activeChildId) {
        return;
      }
      const key = getCardElementKey(activeChildId, querySelection.giftType);
      if (lastScrolledKeyRef.current === key) {
        return;
      }
      const activeCard = document.querySelector(`#${CSS.escape(key)}`);
      if (!activeCard) {
        return;
      }
      lastScrolledKeyRef.current = key;
      activeCard.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    };
    t21 = [querySelection, selectedGiftOptions, activeChildId];
    $[57] = activeChildId;
    $[58] = querySelection;
    $[59] = selectedGiftOptions;
    $[60] = t20;
    $[61] = t21;
  } else {
    t20 = $[60];
    t21 = $[61];
  }
  useEffect(t20, t21);
  const t22 = eligibleBulkGiftSelections.length > 0;
  let t23;
  if ($[62] !== activeSelection || $[63] !== childGiftOptions || $[64] !== giftLimits || $[65] !== handleGiftOptionsOpenChange || $[66] !== handleGiftOptionsSubmit || $[67] !== handleGiftRemove || $[68] !== hasCommitments || $[69] !== hasOnlyCorrespondents || $[70] !== isError || $[71] !== isGiftListLoading || $[72] !== isLoading || $[73] !== selectedGiftTypeLimits || $[74] !== shouldShowFinalGiftSection || $[75] !== shouldShowOtherGiftSections || $[76] !== t22) {
    t23 = {
      isLoading,
      isGiftListLoading,
      isError,
      hasCommitments,
      hasOnlyCorrespondents,
      giftLimits,
      childGiftOptions,
      shouldShowFinalGiftSection,
      shouldShowOtherGiftSections,
      activeSelection,
      selectedGiftTypeLimits,
      showAddGiftToAllEligible: t22,
      handleGiftOptionsOpen,
      handleGiftOptionsSubmit,
      handleGiftRemove,
      handleGiftOptionsOpenChange
    };
    $[62] = activeSelection;
    $[63] = childGiftOptions;
    $[64] = giftLimits;
    $[65] = handleGiftOptionsOpenChange;
    $[66] = handleGiftOptionsSubmit;
    $[67] = handleGiftRemove;
    $[68] = hasCommitments;
    $[69] = hasOnlyCorrespondents;
    $[70] = isError;
    $[71] = isGiftListLoading;
    $[72] = isLoading;
    $[73] = selectedGiftTypeLimits;
    $[74] = shouldShowFinalGiftSection;
    $[75] = shouldShowOtherGiftSections;
    $[76] = t22;
    $[77] = t23;
  } else {
    t23 = $[77];
  }
  return t23;
};
function _temp(c) {
  return !isSponsorshipInTransition(c);
}